import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { buildApiUrl } from '../../utils/apiBase.js';
import {
  getNextSchedulableSlot,
  toDatetimeLocalValue,
  roundToNearest30Minutes,
  isOn30MinuteBoundary,
} from '../../utils/emailScheduleUtils.js';
import AdminRepository from '../../services/AdminRepository.js';
import { AdminTableShimmer } from '../common/Shimmer.jsx';
import { useDestructiveAction } from '../../contexts/DestructiveActionContext.jsx';
import { useAdminDialog } from '../../contexts/AdminDialogContext.jsx';
import { useScreenProbe } from '../../utils/screenTiming.js';
import './AdminAnnouncementManagement.css';

const MAX_INDIVIDUAL_CLINIC_TARGETS = 50;

const CATEGORIES = [
  { value: 'update', label: 'アップデート' },
  { value: 'feature', label: '新機能' },
  { value: 'bugfix', label: '不具合・修正' },
  { value: 'alert', label: 'アラート' },
  { value: 'maintenance', label: 'メンテナンス' },
  { value: 'announcement', label: 'お知らせ' },
  { value: 'notice', label: '告知' },
];

const PRIORITIES = [
  { value: 'normal', label: '通常' },
  { value: 'high', label: '重要' }
];

const categoryLabel = (v) => CATEGORIES.find(c => c.value === v)?.label || v;
const priorityLabel = (v) => PRIORITIES.find(p => p.value === v)?.label || v;
const defaultEmailImportant = (category) => category !== 'maintenance';
/** サーバー isEmailBroadcastSettled と同期（再配信 UI を抑止する状態） */
const isEmailBroadcastSettledStatus = (status) => (
  status === 'sent' || status === 'sending' || status === 'partial'
);

const formatIndividualEmailStatus = (log) => {
  if (!log?.emailBroadcast) return null;
  const sent = log.emailSentCount || 0;
  const skipped = log.emailSkippedCount || 0;
  if (sent > 0 && skipped > 0) return { label: `送信${sent}/スキップ${skipped}`, kind: 'mixed' };
  if (sent > 0) return { label: `送信${sent}件`, kind: 'sent' };
  if (skipped > 0) return { label: `スキップ${skipped}件`, kind: 'skipped' };
  return { label: '—', kind: 'none' };
};

const emailSkipReasonLabel = (reason) => {
  const map = {
    'opt-out': '通知設定オフ',
    'no-email': 'メール未設定',
    'smtp-not-configured': 'SMTP未設定',
  };
  return map[reason] || reason || '—';
};

const formatDate = (ts) => {
  if (!ts) return '—';
  const d = ts.seconds ? new Date(ts.seconds * 1000)
    : ts._seconds ? new Date(ts._seconds * 1000)
    : new Date(ts);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const tsToDatetimeLocal = (ts) => {
  if (!ts) return '';
  const d = ts.seconds ? new Date(ts.seconds * 1000)
    : ts._seconds ? new Date(ts._seconds * 1000)
    : new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return toDatetimeLocalValue(d);
};

const emptyForm = () => ({
  title: '',
  body: '',
  category: 'update',
  priority: 'normal',
  isPublished: false,
  hubPublishMode: 'draft', // draft | immediate | scheduled | with_email_schedule
  hubPublishScheduledAt: '',
  emailBroadcast: false,
  emailImportant: true,
  emailScheduleType: 'immediate',
  emailScheduledAt: '',
  emailMaintenanceWindow: '',
});

const hubPublishLabel = (form) => {
  if (form.emailBroadcast && form.emailScheduleType === 'scheduled') {
    if (form.hubPublishMode === 'with_email_schedule') {
      return form.emailScheduledAt
        ? `${form.emailScheduledAt.replace('T', ' ')} に合わせて公開`
        : 'メール予約時刻に合わせて公開';
    }
    return '即時公開';
  }
  if (form.hubPublishMode === 'scheduled' && form.hubPublishScheduledAt) {
    return `${form.hubPublishScheduledAt.replace('T', ' ')} に予約公開`;
  }
  if (form.hubPublishMode === 'immediate' || form.isPublished) return '即時公開';
  return '非公開のまま';
};

const AdminAnnouncementManagement = ({ clinics = [] }) => {
  const probe = useScreenProbe('announcements');
  const { requestDestructiveAction } = useDestructiveAction();
  const { showAlert } = useAdminDialog();
  const adminRepo = AdminRepository.getInstance();
  const [announcements, setAnnouncements] = useState([]);
  const [individualLogs, setIndividualLogs] = useState([]);
  const [listTab, setListTab] = useState('global');
  const [loading, setLoading] = useState(true);
  const [individualLogsLoading, setIndividualLogsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clinicDirectory, setClinicDirectory] = useState(() => adminRepo.clinicsCache || []);
  const [showModal, setShowModal] = useState(false);
  const [showIndividualModal, setShowIndividualModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [closing, setClosing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState(emptyForm);
  const [individualForm, setIndividualForm] = useState({
    title: '', body: '', category: 'update', priority: 'normal', emailBroadcast: false, emailImportant: true,
  });
  const [selectedClinics, setSelectedClinics] = useState([]);
  const [clinicSearchQuery, setClinicSearchQuery] = useState('');
  const [clinicSearchResults, setClinicSearchResults] = useState([]);
  const [clinicSearchLoading, setClinicSearchLoading] = useState(false);
  const [showIndividualConfirm, setShowIndividualConfirm] = useState(false);
  const [showGlobalConfirm, setShowGlobalConfirm] = useState(false);
  const [selectedIndividualLog, setSelectedIndividualLog] = useState(null);

  const scheduleMinValue = useMemo(
    () => toDatetimeLocalValue(getNextSchedulableSlot()),
    [showModal]
  );

  const loadAnnouncements = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(buildApiUrl('/api/admin/announcements'), { credentials: 'include' });
      if (!res.ok) throw new Error('取得失敗');
      const data = await res.json();
      setAnnouncements(data.announcements || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadIndividualLogs = useCallback(async () => {
    setIndividualLogsLoading(true);
    try {
      const res = await fetch(buildApiUrl('/api/admin/notifications/individual'), { credentials: 'include' });
      if (!res.ok) throw new Error('個別送信履歴の取得に失敗しました');
      const data = await res.json();
      setIndividualLogs(data.logs || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setIndividualLogsLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      probe.start('load');
      try {
        await Promise.all([loadAnnouncements(), loadIndividualLogs()]);
        if (cancelled) return;
        probe.end('load', 'ok');
        probe.unlock();
      } catch (err) {
        if (cancelled) return;
        probe.end('load', `error: ${err?.message || 'unknown'}`);
        probe.unlock();
      }
    })();
    return () => { cancelled = true; };
  }, [loadAnnouncements, loadIndividualLogs, probe]);

  useEffect(() => {
    if (clinics.length > 0) return;
    let cancelled = false;
    adminRepo.getAllClinics()
      .then((list) => {
        if (!cancelled) setClinicDirectory(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setClinicDirectory([]);
      });
    return () => { cancelled = true; };
  }, [adminRepo, clinics.length]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    const hasEmailSchedule = item.emailScheduledAt && item.emailBroadcastStatus === 'scheduled';
    const emailScheduledAtLocal = hasEmailSchedule ? tsToDatetimeLocal(item.emailScheduledAt) : '';
    const hubScheduledAtLocal = item.hubPublishStatus === 'scheduled'
      ? tsToDatetimeLocal(item.hubPublishScheduledAt)
      : '';

    let hubPublishMode = 'draft';
    if (hasEmailSchedule && item.hubPublishTiming === 'with_schedule') {
      hubPublishMode = 'with_email_schedule';
    } else if (hasEmailSchedule && item.isPublished) {
      hubPublishMode = 'immediate';
    } else if (hasEmailSchedule && item.hubPublishStatus === 'scheduled') {
      hubPublishMode = 'with_email_schedule';
    } else if (item.hubPublishTiming === 'scheduled' || item.hubPublishStatus === 'scheduled') {
      hubPublishMode = 'scheduled';
    } else if (item.isPublished) {
      hubPublishMode = 'immediate';
    }

    setForm({
      title: item.title,
      body: item.body,
      category: item.category,
      priority: item.priority,
      isPublished: item.isPublished,
      hubPublishMode,
      hubPublishScheduledAt: hubScheduledAtLocal,
      emailBroadcast: item.emailBroadcast || false,
      emailImportant: typeof item.emailImportant === 'boolean'
        ? item.emailImportant
        : defaultEmailImportant(item.category || 'update'),
      emailScheduleType: hasEmailSchedule ? 'scheduled' : 'immediate',
      emailScheduledAt: emailScheduledAtLocal,
      emailMaintenanceWindow: item.emailMaintenanceWindow || '',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setClosing(true);
    setShowGlobalConfirm(false);
    setTimeout(() => { setShowModal(false); setClosing(false); }, 200);
  };

  const closeIndividualModal = () => {
    setClosing(true);
    setShowIndividualConfirm(false);
    setClinicSearchQuery('');
    setClinicSearchResults([]);
    setTimeout(() => { setShowIndividualModal(false); setClosing(false); }, 200);
  };

  const clinicOptions = clinics.length > 0 ? clinics : clinicDirectory;
  const selectedClinicIdSet = useMemo(
    () => new Set(selectedClinics.map((c) => c.id)),
    [selectedClinics]
  );

  const enrichClinicRecord = useCallback((clinic) => {
    if (!clinic?.id) return null;
    const fromDirectory = clinicOptions.find((c) => c.id === clinic.id);
    return {
      id: clinic.id,
      name: clinic.name || fromDirectory?.name || clinic.id,
      email: clinic.email ?? fromDirectory?.email ?? '',
    };
  }, [clinicOptions]);

  const addSelectedClinic = useCallback((clinic) => {
    const record = enrichClinicRecord(clinic);
    if (!record || selectedClinicIdSet.has(record.id)) return;
    if (selectedClinics.length >= MAX_INDIVIDUAL_CLINIC_TARGETS) return;
    setSelectedClinics((prev) => [...prev, record]);
    setClinicSearchQuery('');
    setClinicSearchResults([]);
  }, [enrichClinicRecord, selectedClinicIdSet, selectedClinics.length]);

  const removeSelectedClinic = useCallback((clinicId) => {
    setSelectedClinics((prev) => prev.filter((c) => c.id !== clinicId));
  }, []);

  useEffect(() => {
    if (!showIndividualModal) return undefined;
    const q = clinicSearchQuery.trim();
    if (!q) {
      setClinicSearchResults([]);
      setClinicSearchLoading(false);
      return undefined;
    }
    let cancelled = false;
    setClinicSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const data = await adminRepo.searchClinics(q, 30);
        if (cancelled) return;
        const list = (data?.clinics || []).filter((c) => !selectedClinicIdSet.has(c.id));
        setClinicSearchResults(list);
      } catch {
        if (!cancelled) setClinicSearchResults([]);
      } finally {
        if (!cancelled) setClinicSearchLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [adminRepo, clinicSearchQuery, showIndividualModal, selectedClinicIdSet]);

  const localClinicMatches = useMemo(() => {
    const q = clinicSearchQuery.trim().toLowerCase();
    if (!q) return [];
    return clinicOptions
      .filter((c) => {
        if (selectedClinicIdSet.has(c.id)) return false;
        const name = String(c.name || '').toLowerCase();
        const id = String(c.id || '').toLowerCase();
        return name.includes(q) || id.includes(q);
      })
      .slice(0, 30);
  }, [clinicOptions, clinicSearchQuery, selectedClinicIdSet]);

  const clinicPickerResults = clinicSearchQuery.trim()
    ? (clinicSearchResults.length > 0 ? clinicSearchResults : localClinicMatches)
    : [];

  const validateScheduleFields = () => {
    if (
      form.emailBroadcast
      && form.emailScheduleType === 'scheduled'
    ) {
      if (!form.emailScheduledAt) {
        setError('メール送信予定日時を指定してください');
        return false;
      }
      if (!isOn30MinuteBoundary(form.emailScheduledAt)) {
        setError('予約送信時刻は30分刻み（:00 または :30）で指定してください');
        return false;
      }
    }
    if (
      !form.emailBroadcast
      && form.hubPublishMode === 'scheduled'
    ) {
      if (!form.hubPublishScheduledAt) {
        setError('Hub 公開予定日時を指定してください');
        return false;
      }
      if (!isOn30MinuteBoundary(form.hubPublishScheduledAt)) {
        setError('Hub 公開予約時刻は30分刻み（:00 または :30）で指定してください');
        return false;
      }
    }
    return true;
  };

  const buildHubPublishPayload = () => {
    const emailScheduled = form.emailBroadcast && form.emailScheduleType === 'scheduled';
    if (emailScheduled) {
      const withSchedule = form.hubPublishMode !== 'immediate';
      return {
        hubPublishTiming: withSchedule ? 'with_schedule' : 'immediate',
        hubPublishScheduledAt: null,
        isPublished: withSchedule ? false : true,
      };
    }
    // メール即時送信中は Hub 単独予約不可
    if (form.hubPublishMode === 'scheduled' && !form.emailBroadcast) {
      return {
        hubPublishTiming: 'scheduled',
        hubPublishScheduledAt: form.hubPublishScheduledAt
          ? new Date(form.hubPublishScheduledAt).toISOString()
          : null,
        isPublished: false,
      };
    }
    if (form.hubPublishMode === 'immediate') {
      return {
        hubPublishTiming: 'immediate',
        hubPublishScheduledAt: null,
        isPublished: true,
      };
    }
    return {
      hubPublishTiming: null,
      hubPublishScheduledAt: null,
      isPublished: false,
    };
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.body.trim()) return;
    if (!validateScheduleFields()) return;
    setSaving(true);
    setShowGlobalConfirm(false);
    try {
      const url = editing
        ? buildApiUrl(`/api/admin/announcements/${editing.id}`)
        : buildApiUrl('/api/admin/announcements');
      const method = editing ? 'PUT' : 'POST';
      const hubPayload = buildHubPublishPayload();
      const payload = {
        title: form.title,
        body: form.body,
        category: form.category,
        priority: form.priority,
        ...hubPayload,
        emailBroadcast: form.emailBroadcast,
        emailImportant: form.emailImportant,
        emailScheduledAt: form.emailBroadcast && form.emailScheduleType === 'scheduled' && form.emailScheduledAt
          ? new Date(form.emailScheduledAt).toISOString()
          : null,
        emailMaintenanceWindow: form.category === 'maintenance' ? form.emailMaintenanceWindow : null,
      };
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || '保存に失敗しました');
      }
      closeModal();
      loadAnnouncements();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const requestSaveGlobal = () => {
    if (!form.title.trim() || !form.body.trim()) return;
    if (!validateScheduleFields()) return;
    const alreadySent = editing && isEmailBroadcastSettledStatus(editing.emailBroadcastStatus);
    if (form.emailBroadcast && !alreadySent) {
      setShowGlobalConfirm(true);
      return;
    }
    handleSave();
  };

  const handleDelete = async (id) => {
    const result = await requestDestructiveAction({
      title: 'お知らせの削除',
      message: 'このお知らせを完全に削除します。この操作は取り消せません。',
      confirmLabel: '削除する',
    });
    if (!result?.reason) return;
    try {
      const res = await fetch(buildApiUrl(`/api/admin/announcements/${id}`), {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: result.reason }),
      });
      if (!res.ok) throw new Error('削除に失敗しました');
      loadAnnouncements();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleTogglePublish = async (item) => {
    try {
      const res = await fetch(buildApiUrl(`/api/admin/announcements/${item.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ isPublished: !item.isPublished })
      });
      if (!res.ok) throw new Error('更新に失敗しました');
      loadAnnouncements();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSaveIndividual = async () => {
    if (selectedClinics.length === 0 || !individualForm.title.trim() || !individualForm.body.trim()) return;
    setSaving(true);
    setShowIndividualConfirm(false);
    const payload = {
      title: individualForm.title.trim(),
      body: individualForm.body.trim(),
      category: individualForm.category,
      priority: individualForm.priority,
      emailBroadcast: individualForm.emailBroadcast,
      emailImportant: individualForm.emailImportant,
      clinicIds: selectedClinics.map((c) => c.id),
    };
    try {
      const res = await fetch(buildApiUrl('/api/admin/notifications/individual'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || '作成に失敗しました');
      }
      const data = await res.json();
      const results = data.results || [];
      const createdCount = data.createdCount ?? results.filter((r) => r.success).length;
      const failedCount = data.failedCount ?? results.filter((r) => !r.success).length;
      const failedIds = new Set(results.filter((r) => !r.success).map((r) => r.clinicId));
      const emailSkippedCount = results.filter((r) => r.success && r.emailSkipped).length;

      if (failedCount > 0 && createdCount > 0) {
        setSelectedClinics((prev) => prev.filter((c) => failedIds.has(c.id)));
        const skipNote = individualForm.emailBroadcast && emailSkippedCount > 0
          ? `（${emailSkippedCount}件はメールをスキップ）`
          : '';
        await showAlert({
          title: '一部作成完了',
          variant: 'warning',
          message: `${createdCount}件のクリニックに個別お知らせを作成しました。${failedCount}件は失敗したため選択に残しています${skipNote}`,
        });
        loadIndividualLogs();
        return;
      }

      if (failedCount > 0) {
        throw new Error(data.message || `${failedCount}件のクリニックで作成に失敗しました`);
      }

      const skipNote = individualForm.emailBroadcast && emailSkippedCount > 0
        ? `${emailSkippedCount}件はメールをスキップしました。`
        : '';
      await showAlert({
        title: '作成完了',
        variant: 'success',
        message: `${createdCount}件のクリニックに個別お知らせを作成しました。${skipNote}`.trim(),
      });
      closeIndividualModal();
      setSelectedClinics([]);
      setListTab('individual');
      loadIndividualLogs();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const requestSaveIndividual = () => {
    if (selectedClinics.length === 0 || !individualForm.title.trim() || !individualForm.body.trim()) return;
    if (selectedClinics.length > MAX_INDIVIDUAL_CLINIC_TARGETS) {
      setError(`一度に送信できるクリニックは${MAX_INDIVIDUAL_CLINIC_TARGETS}件までです`);
      return;
    }
    if (individualForm.emailBroadcast) {
      setShowIndividualConfirm(true);
      return;
    }
    handleSaveIndividual();
  };

  const openIndividualModal = () => {
    setIndividualForm({
      title: '', body: '', category: 'update', priority: 'normal', emailBroadcast: false, emailImportant: true,
    });
    setSelectedClinics([]);
    setClinicSearchQuery('');
    setClinicSearchResults([]);
    setShowIndividualConfirm(false);
    setShowIndividualModal(true);
  };

  return (
    <>
      <div className="section-header">
        <h1>お知らせ管理</h1>
        <p>全体お知らせと個別通知の管理</p>
      </div>

      {error && (
        <div className="admin-announcement-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      <div className="admin-announcement-actions">
        <button className="btn btn-primary" onClick={openCreate}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 18, height: 18 }}>
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          全体お知らせ作成
        </button>
        <button className="btn btn-secondary" onClick={openIndividualModal}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 18, height: 18 }}>
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          個別お知らせ作成
        </button>
      </div>

      <div className="admin-announcement-tabs" role="tablist" aria-label="お知らせ一覧">
        <button
          type="button"
          role="tab"
          aria-selected={listTab === 'global'}
          className={`admin-announcement-tab${listTab === 'global' ? ' active' : ''}`}
          onClick={() => setListTab('global')}
        >
          全体お知らせ
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={listTab === 'individual'}
          className={`admin-announcement-tab${listTab === 'individual' ? ' active' : ''}`}
          onClick={() => setListTab('individual')}
        >
          個別送信履歴
        </button>
      </div>

      {listTab === 'global' && (loading ? (
        <AdminTableShimmer rows={5} columns={3} />
      ) : (
        <div className="admin-announcement-list">
          <table className="admin-table">
            <thead>
              <tr>
                <th>タイトル</th>
                <th>カテゴリ</th>
                <th>優先度</th>
                <th>公開</th>
                <th>メール</th>
                <th>作成日</th>
                <th>削除</th>
              </tr>
            </thead>
            <tbody>
              {announcements.length === 0 ? (
                <tr><td colSpan="7" style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>お知らせはありません</td></tr>
              ) : announcements.map(item => (
                <tr key={item.id} onClick={() => openEdit(item)}>
                  <td className="admin-announcement-title-cell">{item.title}</td>
                  <td className="td-center"><span className={`admin-cat-badge cat-${item.category}`}>{categoryLabel(item.category)}</span></td>
                  <td className="td-center"><span className={`admin-priority-badge priority-${item.priority}`}>{priorityLabel(item.priority)}</span></td>
                  <td className="td-center">
                    {item.hubPublishStatus === 'scheduled' && !item.isPublished ? (
                      <span
                        className="admin-email-badge email-scheduled"
                        title={`Hub公開予約: ${formatDate(item.hubPublishScheduledAt)}（クリックで編集）`}
                      >
                        Hub予約中
                      </span>
                    ) : (
                      <button
                        className={`admin-publish-toggle ${item.isPublished ? 'published' : ''}`}
                        onClick={(e) => { e.stopPropagation(); handleTogglePublish(item); }}
                        title={item.isPublished ? '非公開にする' : '公開する'}
                      >
                        {item.isPublished ? '公開中' : '非公開'}
                      </button>
                    )}
                  </td>
                  <td className="td-center">
                    {item.emailBroadcast ? (
                      <span className={`admin-email-badge email-${item.emailBroadcastStatus || 'none'}`}>
                        {item.emailBroadcastStatus === 'sent' ? `キュー投入済(${item.emailEnqueuedCount ?? item.emailSentCount ?? 0}件)` :
                         item.emailBroadcastStatus === 'partial' ? `一部失敗(Tasks ${item.emailTaskFailedCount ?? 0})` :
                         item.emailBroadcastStatus === 'scheduled' ? '予約中' :
                         item.emailBroadcastStatus === 'sending' ? '投入中...' :
                         item.emailBroadcastStatus === 'failed' ? '失敗' : '—'}
                      </span>
                    ) : (
                      <span style={{ color: '#9ca3af', fontSize: '12px' }}>—</span>
                    )}
                  </td>
                  <td className="admin-announcement-date">{formatDate(item.createdAt)}</td>
                  <td className="td-center">
                    <button
                      className="admin-announcement-delete-btn"
                      onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                      title="削除"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}>
                        <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {listTab === 'individual' && (individualLogsLoading ? (
        <AdminTableShimmer rows={5} columns={3} />
      ) : (
        <div className="admin-announcement-list">
          <table className="admin-table">
            <thead>
              <tr>
                <th>タイトル</th>
                <th>カテゴリ</th>
                <th>優先度</th>
                <th>対象</th>
                <th>メール</th>
                <th>送信日</th>
              </tr>
            </thead>
            <tbody>
              {individualLogs.length === 0 ? (
                <tr><td colSpan="6" style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>個別送信履歴はありません</td></tr>
              ) : individualLogs.map((log) => {
                const emailStatus = formatIndividualEmailStatus(log);
                return (
                  <tr key={log.id} onClick={() => setSelectedIndividualLog(log)}>
                    <td className="admin-announcement-title-cell">{log.title}</td>
                    <td className="td-center"><span className={`admin-cat-badge cat-${log.category}`}>{categoryLabel(log.category)}</span></td>
                    <td className="td-center"><span className={`admin-priority-badge priority-${log.priority}`}>{priorityLabel(log.priority)}</span></td>
                    <td className="td-center">
                      {log.createdCount ?? 0}件
                      {(log.failedCount ?? 0) > 0 && (
                        <span className="admin-individual-failed-note">（{log.failedCount}件失敗）</span>
                      )}
                    </td>
                    <td className="td-center">
                      {!log.emailBroadcast ? (
                        <span style={{ color: '#9ca3af', fontSize: '12px' }}>—</span>
                      ) : (
                        <span className={`admin-email-badge email-${emailStatus?.kind === 'sent' || emailStatus?.kind === 'mixed' ? 'sent' : emailStatus?.kind === 'skipped' ? 'failed' : 'none'}`}>
                          {emailStatus?.label || '—'}
                        </span>
                      )}
                    </td>
                    <td className="admin-announcement-date">{formatDate(log.createdAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}

      {/* 全体お知らせ作成/編集モーダル */}
      {showModal && (
        <div className={`modal-overlay${closing ? ' closing' : ''}`}>
          <div className="modal modal-wide admin-announcement-modal">
            <div className="modal-header">
              <h2>{editing ? 'お知らせを編集' : '全体お知らせを作成'}</h2>
              <button className="modal-close" onClick={closeModal}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>タイトル <span className="required">*</span></label>
                <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="お知らせのタイトル" />
              </div>
              <div className="form-group">
                <label>本文 <span className="required">*</span></label>
                <textarea rows={8} value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} placeholder="お知らせの本文" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>カテゴリ</label>
                  <select
                    value={form.category}
                    onChange={(e) => {
                      const category = e.target.value;
                      setForm({
                        ...form,
                        category,
                        emailImportant: defaultEmailImportant(category),
                      });
                    }}
                  >
                    {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>優先度</label>
                  <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                    {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
              </div>
              {/* メール一斉送信設定 */}
              {(() => {
                const alreadySent = editing && isEmailBroadcastSettledStatus(editing.emailBroadcastStatus);
                return (
                  <div className="admin-email-broadcast-section">
                    <div className="admin-email-broadcast-header">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}>
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                        <polyline points="22,6 12,13 2,6" />
                      </svg>
                      <span>メール一斉送信</span>
                    </div>
                    {alreadySent ? (
                      <p className={`admin-email-broadcast-sent${editing.emailBroadcastStatus === 'partial' ? ' is-partial' : ''}`}>
                        {editing.emailBroadcastStatus === 'partial'
                          ? `キュー投入に一部失敗（outbox ${editing.emailEnqueuedCount ?? 0}件 / Tasks成功 ${editing.emailTaskCreatedCount ?? 0} / 失敗 ${editing.emailTaskFailedCount ?? 0}）。再配信は運用対応が必要です。`
                          : `キュー投入済み（${editing.emailEnqueuedCount ?? editing.emailSentCount ?? 0}件${editing.emailFailedCount ? `、${editing.emailFailedCount}件失敗` : ''}）`}
                      </p>
                    ) : (
                      <>
                        <div className="form-group admin-form-toggle-group">
                          <div className="admin-form-toggle-row">
                            <div className="admin-toggle-caption">
                              <span className="admin-toggle-title">メールでも送信する</span>
                              <span className="form-hint">オンにすると全クリニックの登録メールアドレスへ一斉送信します。</span>
                            </div>
                            <button
                              type="button"
                              role="switch"
                              aria-label="メールでも送信する"
                              aria-checked={form.emailBroadcast}
                              className={`admin-toggle-switch${form.emailBroadcast ? ' on' : ''}`}
                              onClick={() => {
                                const next = !form.emailBroadcast;
                                if (next) {
                                  setForm({
                                    ...form,
                                    emailBroadcast: true,
                                    // 日時指定へ切り替えたときのデフォルトは予約連動
                                    hubPublishMode: form.emailScheduleType === 'scheduled'
                                      ? 'with_email_schedule'
                                      : form.hubPublishMode === 'scheduled'
                                        ? 'draft'
                                        : form.hubPublishMode,
                                    hubPublishScheduledAt: form.emailScheduleType === 'scheduled'
                                      ? ''
                                      : form.hubPublishScheduledAt,
                                  });
                                } else {
                                  setForm({
                                    ...form,
                                    emailBroadcast: false,
                                    hubPublishMode: form.hubPublishMode === 'with_email_schedule'
                                      ? 'draft'
                                      : form.hubPublishMode,
                                  });
                                }
                              }}
                            >
                              <span className="admin-toggle-knob" />
                            </button>
                          </div>
                        </div>
                        <div className="form-group admin-form-toggle-group">
                          <div className="admin-form-toggle-row">
                            <div className="admin-toggle-caption">
                              <span className="admin-toggle-title">重要メールとして送信</span>
                              <span className="form-hint">オンにすると通知設定に関わらず送信され、重要お知らせ用の注記が付きます。オフの場合は通常のお知らせメール（news）として送信します。</span>
                            </div>
                            <button
                              type="button"
                              role="switch"
                              aria-label="重要メールとして送信"
                              aria-checked={form.emailImportant}
                              disabled={!form.emailBroadcast}
                              className={`admin-toggle-switch${form.emailImportant ? ' on' : ''}`}
                              onClick={() => setForm({ ...form, emailImportant: !form.emailImportant })}
                            >
                              <span className="admin-toggle-knob" />
                            </button>
                          </div>
                        </div>
                        {form.emailBroadcast && (
                          <>
                            <div className="form-group">
                              <label>送信タイミング</label>
                              <div className="admin-radio-group">
                                <label className="admin-radio-label">
                                  <input type="radio" name="emailScheduleType" value="immediate"
                                    checked={form.emailScheduleType === 'immediate'}
                                    onChange={() => setForm({
                                      ...form,
                                      emailScheduleType: 'immediate',
                                      emailScheduledAt: '',
                                      hubPublishMode: form.hubPublishMode === 'with_email_schedule'
                                        ? (form.isPublished ? 'immediate' : 'draft')
                                        : form.hubPublishMode,
                                    })} />
                                  <span>即時送信（保存と同時）</span>
                                </label>
                                <label className="admin-radio-label">
                                  <input type="radio" name="emailScheduleType" value="scheduled"
                                    checked={form.emailScheduleType === 'scheduled'}
                                    onChange={() => setForm({
                                      ...form,
                                      emailScheduleType: 'scheduled',
                                      emailScheduledAt: form.emailScheduledAt || scheduleMinValue,
                                      hubPublishMode: 'with_email_schedule',
                                      isPublished: false,
                                      hubPublishScheduledAt: '',
                                    })} />
                                  <span>日時を指定して送信</span>
                                </label>
                              </div>
                            </div>
                            {form.emailScheduleType === 'scheduled' && (
                              <div className="form-group">
                                <label>送信予定日時（30分刻み）</label>
                                <input
                                  type="datetime-local"
                                  step="1800"
                                  value={form.emailScheduledAt}
                                  onChange={(e) => setForm({
                                    ...form,
                                    emailScheduledAt: roundToNearest30Minutes(e.target.value),
                                  })}
                                  min={scheduleMinValue}
                                />
                                <span className="form-hint">:00 または :30 に自動で丸められます。Scheduler は15分ごとに実行されます。</span>
                              </div>
                            )}
                            {form.category === 'maintenance' && (
                              <div className="form-group">
                                <label>メンテナンス実施予定期間（メール本文用）</label>
                                <input type="text" value={form.emailMaintenanceWindow}
                                  onChange={e => setForm({ ...form, emailMaintenanceWindow: e.target.value })}
                                  placeholder="例: 2026年4月1日 02:00〜06:00" />
                              </div>
                            )}
                          </>
                        )}
                        {showGlobalConfirm && (
                          <div className="admin-individual-confirm">
                            <p><strong>メール送信の確認</strong></p>
                            <p>対象: 全クリニック（一斉送信）</p>
                            <p>件名: {form.title}</p>
                            <p>メール種別: {form.emailImportant ? '重要メール（通知設定を無視して送信）' : '通常のお知らせメール（news 設定を尊重）'}</p>
                            <p>
                              送信タイミング:{' '}
                              {form.emailScheduleType === 'scheduled' && form.emailScheduledAt
                                ? `${form.emailScheduledAt.replace('T', ' ')}（予約）`
                                : '即時（保存と同時）'}
                            </p>
                            <p>Hub公開: {hubPublishLabel(form)}</p>
                            {form.category === 'maintenance' && form.emailMaintenanceWindow && (
                              <p>メンテナンス期間: {form.emailMaintenanceWindow}</p>
                            )}
                            <p>この内容でアプリ内お知らせとメールを送信します。よろしいですか？</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })()}

              {/* Hub 公開（メール設定の後に配置し、予約連動を選びやすくする） */}
              {(() => {
                const emailScheduled = form.emailBroadcast && form.emailScheduleType === 'scheduled';
                const allowHubOnlySchedule = !form.emailBroadcast;
                return (
                  <div className="admin-email-broadcast-section">
                    <div className="admin-email-broadcast-header">
                      <span>Hub 公開</span>
                    </div>
                    {emailScheduled ? (
                      <div className="form-group">
                        <label>Hub 公開タイミング</label>
                        <div className="admin-radio-group">
                          <label className="admin-radio-label">
                            <input
                              type="radio"
                              name="hubPublishModeEmail"
                              value="with_email_schedule"
                              checked={form.hubPublishMode !== 'immediate'}
                              onChange={() => setForm({
                                ...form,
                                hubPublishMode: 'with_email_schedule',
                                isPublished: false,
                              })}
                            />
                            <span>メール予約と同じ時刻に Hub 公開（推奨）</span>
                          </label>
                          <label className="admin-radio-label">
                            <input
                              type="radio"
                              name="hubPublishModeEmail"
                              value="immediate"
                              checked={form.hubPublishMode === 'immediate'}
                              onChange={() => setForm({
                                ...form,
                                hubPublishMode: 'immediate',
                                isPublished: true,
                              })}
                            />
                            <span>Hub には即時公開（メールのみ予約）</span>
                          </label>
                        </div>
                        <span className="form-hint">メール送信が失敗しても、予約時刻で Hub は公開されます。</span>
                      </div>
                    ) : (
                      <>
                        <div className="form-group">
                          <label>Hub 公開タイミング</label>
                          <div className="admin-radio-group">
                            <label className="admin-radio-label">
                              <input
                                type="radio"
                                name="hubPublishMode"
                                value="draft"
                                checked={form.hubPublishMode === 'draft'}
                                onChange={() => setForm({
                                  ...form,
                                  hubPublishMode: 'draft',
                                  isPublished: false,
                                  hubPublishScheduledAt: '',
                                })}
                              />
                              <span>非公開のまま保存</span>
                            </label>
                            <label className="admin-radio-label">
                              <input
                                type="radio"
                                name="hubPublishMode"
                                value="immediate"
                                checked={form.hubPublishMode === 'immediate'}
                                onChange={() => setForm({
                                  ...form,
                                  hubPublishMode: 'immediate',
                                  isPublished: true,
                                  hubPublishScheduledAt: '',
                                })}
                              />
                              <span>即時公開</span>
                            </label>
                            {allowHubOnlySchedule && (
                              <label className="admin-radio-label">
                                <input
                                  type="radio"
                                  name="hubPublishMode"
                                  value="scheduled"
                                  checked={form.hubPublishMode === 'scheduled'}
                                  onChange={() => setForm({
                                    ...form,
                                    hubPublishMode: 'scheduled',
                                    isPublished: false,
                                    hubPublishScheduledAt: form.hubPublishScheduledAt || scheduleMinValue,
                                  })}
                                />
                                <span>日時を指定して公開</span>
                              </label>
                            )}
                          </div>
                        </div>
                        {allowHubOnlySchedule && form.hubPublishMode === 'scheduled' && (
                          <div className="form-group">
                            <label>Hub 公開予定日時（30分刻み）</label>
                            <input
                              type="datetime-local"
                              step="1800"
                              value={form.hubPublishScheduledAt}
                              onChange={(e) => setForm({
                                ...form,
                                hubPublishScheduledAt: roundToNearest30Minutes(e.target.value),
                              })}
                              min={scheduleMinValue}
                            />
                            <span className="form-hint">:00 または :30 に自動で丸められます。Scheduler は15分ごとに実行されます。</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>キャンセル</button>
              {showGlobalConfirm ? (
                <>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowGlobalConfirm(false)}
                    disabled={saving}
                  >
                    内容を修正
                  </button>
                  <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                    {saving ? '送信中...' : 'メール送信を確定'}
                  </button>
                </>
              ) : (
                <button
                  className="btn btn-primary"
                  onClick={requestSaveGlobal}
                  disabled={saving || !form.title.trim() || !form.body.trim()}
                >
                  {saving ? '保存中...' : editing ? '変更を反映' : form.emailBroadcast ? '作成（メール確認）' : '作成'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 個別お知らせ作成モーダル */}
      {showIndividualModal && (
        <div className={`modal-overlay${closing ? ' closing' : ''}`}>
          <div className="modal modal-wide admin-announcement-modal">
            <div className="modal-header">
              <h2>個別お知らせを作成</h2>
              <button className="modal-close" onClick={closeIndividualModal}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>対象クリニック <span className="required">*</span></label>
                {selectedClinics.length > 0 && (
                  <div className="admin-clinic-chip-stack">
                    {selectedClinics.map((c) => (
                      <span key={c.id} className="admin-clinic-chip">
                        <span className="admin-clinic-chip-label">{c.name}</span>
                        <span className="admin-clinic-chip-id">{c.id.slice(0, 8)}…</span>
                        <button
                          type="button"
                          className="admin-clinic-chip-remove"
                          onClick={() => removeSelectedClinic(c.id)}
                          aria-label={`${c.name} を削除`}
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <input
                  type="search"
                  className="admin-clinic-search-input"
                  value={clinicSearchQuery}
                  onChange={(e) => setClinicSearchQuery(e.target.value)}
                  placeholder="クリニック名またはIDで検索して追加..."
                  autoComplete="off"
                />
                {clinicSearchQuery.trim() && (
                  <div className="admin-clinic-search-results">
                    {clinicSearchLoading ? (
                      <div className="admin-clinic-search-empty">検索中...</div>
                    ) : clinicPickerResults.length === 0 ? (
                      <div className="admin-clinic-search-empty">該当するクリニックがありません</div>
                    ) : (
                      clinicPickerResults.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          className="admin-clinic-search-item"
                          onClick={() => addSelectedClinic(c)}
                        >
                          <span className="admin-clinic-search-name">{c.name || c.id}</span>
                          <span className="admin-clinic-search-id">{c.id}</span>
                        </button>
                      ))
                    )}
                  </div>
                )}
                {!clinicSearchQuery.trim() && selectedClinics.length === 0 && (
                  <span className="form-hint">検索してクリニックを追加してください（複数選択可）</span>
                )}
                {selectedClinics.length > 0 && (
                  <span className={`form-hint${selectedClinics.length >= MAX_INDIVIDUAL_CLINIC_TARGETS ? ' admin-clinic-limit-reached' : ''}`}>
                    {selectedClinics.length}件選択中（最大{MAX_INDIVIDUAL_CLINIC_TARGETS}件）
                    {selectedClinics.length >= MAX_INDIVIDUAL_CLINIC_TARGETS && ' — 上限に達しました'}
                  </span>
                )}
              </div>
              <div className="form-group">
                <label>タイトル <span className="required">*</span></label>
                <input type="text" value={individualForm.title} onChange={e => setIndividualForm({ ...individualForm, title: e.target.value })} placeholder="お知らせのタイトル" />
              </div>
              <div className="form-group">
                <label>本文 <span className="required">*</span></label>
                <textarea rows={6} value={individualForm.body} onChange={e => setIndividualForm({ ...individualForm, body: e.target.value })} placeholder="お知らせの本文" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>カテゴリ</label>
                  <select
                    value={individualForm.category}
                    onChange={(e) => {
                      const category = e.target.value;
                      setIndividualForm({
                        ...individualForm,
                        category,
                        emailImportant: defaultEmailImportant(category),
                      });
                    }}
                  >
                    {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>優先度</label>
                  <select value={individualForm.priority} onChange={e => setIndividualForm({ ...individualForm, priority: e.target.value })}>
                    {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group admin-form-toggle-group">
                <div className="admin-form-toggle-row">
                  <div className="admin-toggle-caption">
                    <span className="admin-toggle-title">メールでも送信する</span>
                    <span className="form-hint">オンにするとクリニック登録メールアドレスへも送信します。</span>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-label="メールでも送信する"
                    aria-checked={individualForm.emailBroadcast}
                    className={`admin-toggle-switch${individualForm.emailBroadcast ? ' on' : ''}`}
                    onClick={() => setIndividualForm({ ...individualForm, emailBroadcast: !individualForm.emailBroadcast })}
                  >
                    <span className="admin-toggle-knob" />
                  </button>
                </div>
              </div>
              <div className="form-group admin-form-toggle-group">
                <div className="admin-form-toggle-row">
                  <div className="admin-toggle-caption">
                    <span className="admin-toggle-title">重要メールとして送信</span>
                    <span className="form-hint">オンにすると通知設定に関わらず送信され、重要お知らせ用の注記が付きます。オフの場合は通常のお知らせメール（news）として送信します。</span>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-label="重要メールとして送信"
                    aria-checked={individualForm.emailImportant}
                    disabled={!individualForm.emailBroadcast}
                    className={`admin-toggle-switch${individualForm.emailImportant ? ' on' : ''}`}
                    onClick={() => setIndividualForm({ ...individualForm, emailImportant: !individualForm.emailImportant })}
                  >
                    <span className="admin-toggle-knob" />
                  </button>
                </div>
              </div>
              {showIndividualConfirm && (
                <div className="admin-individual-confirm">
                  <p><strong>メール送信の確認</strong></p>
                  <p>対象: {selectedClinics.length}件のクリニック</p>
                  <ul className="admin-individual-confirm-clinics">
                    {selectedClinics.map((c) => (
                      <li key={c.id}>
                        {c.name} — {c.email || '（クリニックメール未設定 — 送信スキップの可能性）'}
                      </li>
                    ))}
                  </ul>
                  <p>件名: {individualForm.title}</p>
                  {individualForm.emailBroadcast && (
                    <p>メール種別: {individualForm.emailImportant ? '重要メール（通知設定を無視して送信）' : '通常のお知らせメール（news 設定を尊重）'}</p>
                  )}
                  <p>この内容でアプリ内お知らせとメールを送信します。よろしいですか？</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeIndividualModal}>キャンセル</button>
              {showIndividualConfirm ? (
                <>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowIndividualConfirm(false)}
                    disabled={saving}
                  >
                    内容を修正
                  </button>
                  <button className="btn btn-primary" onClick={handleSaveIndividual} disabled={saving}>
                    {saving ? '送信中...' : 'メール送信を確定'}
                  </button>
                </>
              ) : (
                <button
                  className="btn btn-primary"
                  onClick={requestSaveIndividual}
                  disabled={
                    saving
                    || selectedClinics.length === 0
                    || selectedClinics.length > MAX_INDIVIDUAL_CLINIC_TARGETS
                    || !individualForm.title.trim()
                    || !individualForm.body.trim()
                  }
                >
                  {saving ? '保存中...' : individualForm.emailBroadcast ? '作成（メール確認）' : '作成'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 個別送信履歴詳細 */}
      {selectedIndividualLog && (
        <div className="modal-overlay" onClick={() => setSelectedIndividualLog(null)}>
          <div
            className="modal modal-wide admin-announcement-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>個別送信履歴の詳細</h2>
              <button type="button" className="modal-close" onClick={() => setSelectedIndividualLog(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="admin-individual-log-meta">
                <p><strong>{selectedIndividualLog.title}</strong></p>
                <p className="admin-individual-log-meta-row">
                  <span className={`admin-cat-badge cat-${selectedIndividualLog.category}`}>{categoryLabel(selectedIndividualLog.category)}</span>
                  <span className={`admin-priority-badge priority-${selectedIndividualLog.priority}`}>{priorityLabel(selectedIndividualLog.priority)}</span>
                  <span>{formatDate(selectedIndividualLog.createdAt)}</span>
                </p>
                {selectedIndividualLog.emailBroadcast && (
                  <p className="form-hint">
                    メール種別: {(selectedIndividualLog.emailImportant ?? defaultEmailImportant(selectedIndividualLog.category))
                      ? '重要メール'
                      : '通常のお知らせメール（news）'}
                  </p>
                )}
                <pre className="admin-individual-log-body">{selectedIndividualLog.body}</pre>
              </div>
              <div className="admin-announcement-list admin-individual-log-results">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>クリニック</th>
                      <th>結果</th>
                      <th>メール</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedIndividualLog.results || []).map((row) => (
                      <tr key={`${row.clinicId}-${row.id || 'fail'}`}>
                        <td>{row.clinicName || row.clinicId}</td>
                        <td className="td-center">
                          {row.success ? (
                            <span className="admin-email-badge email-sent">作成済</span>
                          ) : (
                            <span className="admin-email-badge email-failed">{row.message || '失敗'}</span>
                          )}
                        </td>
                        <td className="td-center">
                          {!selectedIndividualLog.emailBroadcast ? '—' : !row.success ? '—' : row.emailSent ? (
                            <span className="admin-email-badge email-sent">送信</span>
                          ) : row.emailSkipped ? (
                            <span className="admin-email-badge email-failed" title={emailSkipReasonLabel(row.emailSkipReason)}>
                              スキップ
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setSelectedIndividualLog(null)}>閉じる</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminAnnouncementManagement;
